import { mutation } from "gql-query-builder";
import type { ICart } from "../src/queries/cart-gql";

import { gql, useMutation } from "@apollo/client";
import type { CartCodeDiscountAllocation, CartCreatePayload } from "@shopify/hydrogen-react/storefront-api-types";
import { cartDiscountAllocationFragment } from "../src/queries/cart-gql";

interface IProps {
  accessToken: string;
  lines?: { quantity: number; merchandiseId: string }[];
  discountCodes: string[];
  attributes?: { key: string; value: string }[];
}
interface ICartCreateMutation {
  cartCreate: CartCreatePayload & ICart;
}

// 중복으로 할인 적용이 될 경우(멤버십 + 할인코드),
// cart에서 해당 할인코드를 업데이트하여 유효하지 않은 코드로 변경해버림.
// 따라서 할인코드가 유효한지 확인을 할 수 없음

/**
 * 중복으로 적용된 할인코드가 유효한지 판별할 수 있는 방법
 * 1. attribute를 제외한 카트를 새로 생성
 * 2. 새로 만들어진 cart에 할인코드 적용하면 유효한 할인코드인지 반환해줌
 * */
const useValidateDiscountCode = () => {
  // 카트 생성
  const cartCreateMutation = mutation(
    {
      operation: "cartCreate",
      fields: [
        {
          cart: [
            "id",
            { discountCodes: ["applicable", "code"] },

            {
              operation: "lines(first: 20)",
              fields: [
                {
                  edges: [
                    {
                      node: [
                        { discountAllocations: [cartDiscountAllocationFragment] },
                        { merchandise: [{ operation: "ProductVariant", fields: ["id"], fragment: true }] },
                      ],
                    },
                  ],
                },
              ],
              variables: {},
            },
          ],
        },
        { userErrors: ["field", "message"] },
      ],
      variables: {
        input: { type: "CartInput!" },
      },
    },
    undefined,
    { operationName: "CartCreate" },
  );

  const [createCart] = useMutation<ICartCreateMutation>(gql(cartCreateMutation.query), {
    onError: (error) => {
      console.log("error:", error);
      // datadogLogs.logger.error(
      //   "[SHOPIFY][CartCreateInput] Failed to mutation",
      //   error,
      // );
    },
  });

  // 할인코드를 유효성 체크하는 함수
  const validateDiscountCode = async ({
    accessToken,
    lines,
    discountCodes,
    attributes,
  }: IProps): Promise<{
    isAvailableDiscountCode: boolean;
    targetLineItems: string[];
  }> => {
    // 1. 할인코드의 유효성 확인을 위한 가짜 카트를 생성
    // 이 카트에는 attribute를 제외하고 넣어줌)
    // 새로 만든 카트에 할인코드를 적용하여 유효성 검사
    const { data: cartData } = await createCart({
      variables: {
        input: {
          buyerIdentity: { customerAccessToken: accessToken },
          discountCodes,
          lines,
          attributes,
        },
      },
    });

    if (!cartData?.cartCreate.cart.id) {
      return { isAvailableDiscountCode: false, targetLineItems: [] };
    }

    // 2. 할인코드가 유효한지 여부를 반환
    const isAvailableDiscountCode = cartData.cartCreate.cart.discountCodes[0]?.applicable;
    const targetLineItems = cartData.cartCreate.cart.lines.edges
      .filter(({ node }) => {
        const cartCodeDiscountAllocations = node.discountAllocations.filter(
          (item) => item.__typename === "CartCodeDiscountAllocation",
        );
        return (
          cartCodeDiscountAllocations.length > 0 && (cartCodeDiscountAllocations[0] as CartCodeDiscountAllocation).code
        );
      })
      .map(({ node }) => node.merchandise.id);

    return { isAvailableDiscountCode, targetLineItems };
  };

  return { validateDiscountCode };
};

export default useValidateDiscountCode;
